import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { PickFunction } from '@ivymobi/orbit-core';
import type { SignInProps } from '../users';
import ParticleBackground from './components/ParticleBackground';
import LoginPage from './LoginPage';
import SignUpPage from './SignUpPage';
import WechatPage from './WechatPage';
import BeianLink from '../components/BeianLink';

interface UserV2Props extends PickFunction<SignInProps> {
  signup: (signupData: any) => void;
}

export default function UserV2({ login, signup, loginByPassword }: UserV2Props) {
  const [t] = useTranslation();

  return (
    <div
      className="tw-v2-reset tw-min-h-screen tw-relative tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
      style={{ /* fontFamily 由 tw-v2-reset 统一设置 */ }}
    >
      {/* ===== 背景层 ===== */}
      {/* 基础暗色背景 */}
      <div className="tw-absolute tw-inset-0" style={{ backgroundColor: '#0f172a' }} />

      {/* 渐变层 - 红色（左上） */}
      <div
        className="tw-absolute tw-top-0 tw-left-0 tw-rounded-full"
        style={{
          width: '60%',
          height: '50%',
          background: 'radial-gradient(ellipse at center, rgba(220, 38, 38, 0.3) 0%, rgba(220, 38, 38, 0.15) 40%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* 渐变层 - 蓝色（右上） */}
      <div
        className="tw-absolute tw-top-0 tw-right-0 tw-rounded-full"
        style={{
          width: '60%',
          height: '60%',
          background: 'radial-gradient(ellipse at center, rgba(37, 99, 235, 0.3) 0%, rgba(37, 99, 235, 0.15) 40%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* 渐变层 - 紫色（底部） */}
      <div
        className="tw-absolute tw-bottom-0 tw-left-1/4 tw-rounded-full"
        style={{
          width: '70%',
          height: '50%',
          background: 'radial-gradient(ellipse at center, rgba(147, 51, 234, 0.2) 0%, rgba(99, 102, 241, 0.1) 40%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* 粒子动画 */}
      <ParticleBackground
        particleCount={80}
        minRadius={1}
        maxRadius={3}
        speed={0.4}
        lineDistance={140}
      />

      {/* ===== 主内容 ===== */}
      <div className="tw-relative tw-w-full tw-max-w-md tw-px-6" style={{ zIndex: 10 }}>
        {/* Logo 和标题 */}
        <div className="tw-text-center tw-mb-8">
          <div className="tw-inline-flex tw-items-center tw-justify-center tw-mb-5">
            <img
              src="/images/login-logo.png"
              alt="Logo"
              className="tw-h-14 tw-object-contain"
              style={{ filter: 'brightness(1.2)' }}
            />
          </div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-white tw-tracking-tight tw-m-0">
            {t('login.slogan', 'AI产品信息管理平台')}
          </h1>
          <p className="tw-mt-2 tw-text-sm tw-m-0" style={{ color: 'rgba(191, 219, 254, 0.6)' }}>
            企业级智能产品数据管理解决方案
          </p>
        </div>

        {/* 登录卡片 - 毛玻璃效果 */}
        <div
          className="tw-rounded-xl tw-p-6"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Routes>
            <Route
              path="signin"
              element={<LoginPage login={login} loginByPassword={loginByPassword} />}
            />
            <Route
              index
              element={<LoginPage login={login} loginByPassword={loginByPassword} />}
            />
            <Route
              path="signup"
              element={<SignUpPage signup={signup} />}
            />
            <Route
              path="wechat"
              element={<WechatPage />}
            />
          </Routes>
        </div>

        {/* 底部备案 */}
        {!Capacitor.isNativePlatform() && (
          <p
            className="tw-text-center tw-text-xs tw-mt-6 tw-m-0"
            style={{ color: 'rgba(191, 219, 254, 0.4)' }}
          >
            <BeianLink />
          </p>
        )}
      </div>
    </div>
  );
}
